import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {AudioService} from '../../../../services/audio/audio.service';
import {LangTgtService} from '../../../../services/lang-tgt/lang-tgt.service';
import {ExamService} from '../../../../services/exam/exam.service';
import {CommonModule} from "@angular/common";
import {environment} from '../../../../../environments/environment';

@Component({
  selector: 'app-fourth-listening',
  standalone: true,
  imports: [
    CommonModule
  ],
  templateUrl: './fourth-listening.component.html',
  styleUrls: ['./fourth-listening.component.scss']
})
export class FourthListeningComponent implements OnInit, OnDestroy {

  @Input() idLevel: string;
  @Output() eventEmitter = new EventEmitter<string>();
  questions: any[] = [];

  answers: { [key: string]: string | undefined } = {};
  letters: string[] = ['A', 'B', 'C', 'D', 'E', 'F'];
  audioUrls: string[] = [];
  @ViewChild('audioPlayer', { static: true }) audioPlayer!: ElementRef<HTMLAudioElement>;
  isPlaying = false;
  currentTime = 0;
  totalTime = 0;
  currentIndex = 0;
  previousDurations: number[] = [];
  audios: HTMLAudioElement[] = [];
  constructor(
    private audioSrv: AudioService,
    private examSrv: ExamService,
    private langTgtSrv: LangTgtService
  ) { }

  trackByIndex(index: number) { return index; }

  private getAudioSrc(file: string, codeLang?: string): string {
    return `${environment.api.endpoint}assets/audio/uploads/${(codeLang) ? codeLang : this.langTgtSrv.current.code}/${file}`;
  }

  async ngOnInit(): Promise<void> {
    this.questions = await this.examSrv.generate(this.idLevel, 'FOURTH_LISTENING');
    this.questions.forEach((question) => {
      question.questions.forEach((item) => {
        this.answers[item.uniqid] = undefined;
      });
    });

    // Play instructions audio
    await this.audioSrv.play('exam_listening4.mp3');
    // Init audios
    this.initAudioFiles();
    setTimeout(() =>
      {
        this.togglePlayPause();
      },
      24500);
  }

  togglePlayPause(): void {
    const audioElement = this.audioPlayer.nativeElement;
    if (this.isPlaying) {
      audioElement.pause();
    } else {
      if (this.currentIndex === 0 && audioElement.currentTime === 0) {
        audioElement.src = this.audioUrls[this.currentIndex];
      }
      audioElement.play();
    }
    this.isPlaying = !this.isPlaying;
  }

  initAudioFiles(): void {
    this.isPlaying = false;
    this.audioUrls = [];

    this.questions.forEach((question) => {
      // Play dialogue
      this.audioUrls.push(this.getAudioSrc(question.question.audio));
      this.audioUrls.push(this.getAudioSrc('40seconds.mp3'));
    });

    const newAudioElement = document.createElement('audio');
    const oldAudioElement = this.audioPlayer.nativeElement;

    newAudioElement.controls = oldAudioElement.controls;
    newAudioElement.hidden = oldAudioElement.hidden;

    oldAudioElement.parentNode?.replaceChild(newAudioElement, oldAudioElement);

    this.audioPlayer.nativeElement = newAudioElement;

    this.loadCombinedAudio();
  }

  async loadCombinedAudio(): Promise<void> {
    this.totalTime = 0;
    this.previousDurations = [];
    this.currentIndex = 0;
    this.audios = [];

    const audioPromises = this.audioUrls.map(url => this.loadAudioMetadata(url));
    const durations = await Promise.all(audioPromises);

    durations.forEach(duration => {
      this.totalTime += duration;
      this.previousDurations.push(duration);
    });

    this.setupAudioPlayer();
  }

  loadAudioMetadata(url: string): Promise<number> {
    return new Promise((resolve, reject) => {
      const audio = new Audio(url);
      this.audios.push(audio);
      audio.addEventListener('loadedmetadata', () => {
        resolve(audio.duration);
      });
      audio.addEventListener('error', (e) => {
        reject(e);
      });
    });
  }

  setupAudioPlayer(): void {
    const audioElement = this.audioPlayer.nativeElement;
    audioElement.src = this.audioUrls[this.currentIndex];

    audioElement.addEventListener('ended', this.handleAudioEnded.bind(this));
    audioElement.addEventListener('timeupdate', this.updateCurrentTime.bind(this));
    audioElement.addEventListener('play', () => this.isPlaying = true);
    audioElement.addEventListener('pause', () => this.isPlaying = false);
  }

  handleAudioEnded(): void {
    this.currentIndex++;
    if (this.currentIndex < this.audioUrls.length) {
      this.audioPlayer.nativeElement.src = this.audioUrls[this.currentIndex];
      this.audioPlayer.nativeElement.play();
    } else {
      this.next();
    }
  }

  updateCurrentTime(): void {
    let elapsed = 0;
    for (let i = 0; i < this.currentIndex; i++) {
      elapsed += this.previousDurations[i];
    }
    this.currentTime = elapsed + this.audioPlayer.nativeElement.currentTime;
  }

  displayDuration(value: number) {
    const minutes: number = Math.floor(value / 60);
    const seconds: number = Math.floor(value % 60);
    return `${this.padZero(minutes)}:${this.padZero(seconds)}`;
  }

  padZero(num: number): string {
    return num < 10 ? '0' + num : num.toString();
  }

  selectAnswer(question, answer): void {
    this.answers[question.uniqid] = answer.uniqid;
  }

  isSelected(question, answer): boolean {
    return this.answers[question.uniqid] === answer.uniqid;
  }

  isAnswered(question): boolean {
    return this.answers[question.uniqid] !== undefined;
  }

  getIndex(i: number, j: number): number {
    let index = 0;

    // Calculer l'index séquentiel basé sur les boucles imbriquées
    for (let k = 0; k < i; k++) {
      index += this.questions[k].questions.length;
    }

    return index + j + 1;
  }

  next(): void {
    this.eventEmitter.emit('FOURTH_LISTENING');
  }

  async ngOnDestroy(): Promise<void> {
    const audioElement = this.audioPlayer.nativeElement;
    if (this.isPlaying) {
      audioElement.pause();
    }
    audioElement.removeEventListener('ended', this.handleAudioEnded.bind(this));
    audioElement.removeEventListener('timeupdate', this.updateCurrentTime.bind(this));
    this.audioPlayer.nativeElement = null;

    await this.examSrv.submit(this.idLevel, {
      answers: this.answers,
      type: 'FOURTH_LISTENING'
    });
  }
}
